import React, {  useState } from "react";
import { IoCloseCircleOutline, IoSend } from "react-icons/io5";
import { ImAttachment } from "react-icons/im";
import { useStores } from "../../store/index";
import classNames from "classnames";
import { FaExclamationTriangle, FaTimes, FaCheck } from "react-icons/fa";
import {
  AiOutlineBug,
} from "react-icons/ai";


import { uploadFileToAzure } from "../../services/azure";
import { CiCirclePlus } from "react-icons/ci";


const HelpCenter = ({ className }) => {
  const [isFormVisible, setFormVisible] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    attachment: [],
    url: [],
  });

  const [error, setError] = useState({
    title: "",
    description: "",
    file: "",
  });

  const { FeedbackStore } = useStores();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (error[name]) {
      setError({
        ...error,
        [name]: "",
      });
    }
  };

  const toggleForm = () => {
    setFormData({ title: "", description: "", attachment: [], url: [] });
    setError({ title: "", description: "", file: "" });
    setFormVisible((prev) => !prev);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    
    if (files.length === 0) return;

    // Check if adding new files would exceed the limit
    if (formData.attachment.length + files.length > 3) {
      setError({
        ...error,
        file: "You can only upload up to 3 files",
      });
      setTimeout(() => {
        setError({
          ...error,
          file: "",
        });
      }, 3000);
      return;
    }

    // Check file size (max 5MB per file)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    const oversizedFiles = files.filter(file => file.size > maxSize);
    
    if (oversizedFiles.length > 0) {
      setError({
        ...error,
        file: "Each file must be less than 5MB",
      });
      setTimeout(() => {
        setError({
          ...error,
          file: "",
        });
      }, 3000);
      return;
    }

    setFormData(prevFormData => ({
      ...prevFormData,
      attachment: [...prevFormData.attachment, ...files],
    }));
  };

  const removeAttachment = (index) => {
    setFormData(prevData => ({
      ...prevData,
      attachment: prevData.attachment.filter((_, i) => i !== index)
    }));
  };

  const closePopup = () => {
    setSubmissionStatus(null);
  };

  const handleResubmit = () => {
    setSubmissionStatus(null);
    setFormVisible(true);
  };

  const handleCancel = () => {
    setFormData({
      title: "",
      description: "",
      attachment: [],
      url: [],
    });
    setError({ title: "", description: "", file: "" });
    setFormVisible(false);
  };

  const validateForm = () => {
    const newError = {};
    if (!formData.title.trim()) {
      newError.title = "Title is required";
    }
    if (!formData.description.trim()) {
      newError.description = "Description is required";
    }
    setError(newError);
    return Object.keys(newError).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    const files = formData.attachment;
    if (files.length > 0) {
      setLoading(true);
      const urls = [];

      try {
        // Upload all files first
        const uploadPromises = files.map(async (file) => {
          const timestamp = Date.now().toString();
          const min = 1000;
          const max = 9999;
          const randomNumber = Math.floor(Math.random() * (max - min + 1)) + min;
          const filename = `${randomNumber}_${timestamp}_${file.name}`;

          // Upload the file to Azure
          const response = await uploadFileToAzure(filename, file, {
            action: "profile",
          });
          
          if (response?.url) {
            urls.push(response.url);
          }
        });

        // Wait for all file uploads to complete
        await Promise.all(uploadPromises);

        // After files are uploaded, construct the request body
        const body = {
          subject: formData.title,
          body: formData.description,
          ...(urls.length > 0 && { media: urls.map((url) => ({ url })) }),
        };

        // Send the request after uploading the files
        const result = await FeedbackStore.postSuggestionToApi(body);

        if (result) {
          setSubmissionStatus("success");
        } else {
          setSubmissionStatus("failure");
        }
      } catch (error) {
        console.error("Error submitting form:", error);
        setSubmissionStatus("failure");
      } finally {
        // Reset the form
        setFormVisible(false);
        setLoading(false);
        setFormData({ title: "", description: "", attachment: [], url: [] });
      }
    } else {
      // If no files, just submit the form data
      try {
        const body = {
          subject: formData.title,
          body: formData.description,
        };

        const result = await FeedbackStore.postSuggestionToApi(body);

        if (result) {
          setSubmissionStatus("success");
        } else {
          setSubmissionStatus("failure");
        }
      } catch (error) {
        console.error("Error submitting form:", error);
        setSubmissionStatus("failure");
      } finally {
        // Reset the form
        setFormVisible(false);
        setLoading(false);
        setFormData({ title: "", description: "", attachment: [], url: [] });
      }
    }
  };

  return (
    <div>
      {isFormVisible && (
        <div className="fixed inset-0 w-full h-full z-50 backdrop-filter backdrop-blur-sm bg-black bg-opacity-40"></div>
      )}
      
      <div
        className={classNames(
          `fixed z-50
          ${window.location.pathname.includes("/company") 
            ? "bottom-[11%]" 
            : "bottom-[13%]"}
          right-4 md:right-0 `,
          className
        )}
      >
        {isFormVisible ? (
          <AiOutlineBug
            onClick={toggleForm}
            className="bg-primary rounded-full p-2 text-white h-10 w-10 cursor-pointer hover:bg-primary/90 transition-colors"
          />
        ) : (
          <>
            <div
              className="bg-primary text-xs text-white items-center p-2 cursor-pointer md:flex lg:flex hidden md:-mr-7 -rotate-90 hover:bg-primary/90 transition-colors"
              onClick={toggleForm}
            >
              <AiOutlineBug className="text-2xl" />
              <p>Have issue</p>
            </div>
            <div className="md:hidden lg:hidden block">
              <AiOutlineBug
                onClick={toggleForm}
                className="bg-primary rounded-full p-2 text-white h-8 w-8 cursor-pointer hover:bg-primary/90 transition-colors"
              />
            </div>
          </>
        )}

        {isFormVisible && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-semibold text-gray-800">
                    Report Your Issue
                  </h2>
                  <button
                    onClick={toggleForm}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <IoCloseCircleOutline className="h-6 w-6" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label
                      htmlFor="title"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Title of the issue
                    </label>
                    <input
                      type="text"
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
                      placeholder="Enter the title of the issue"
                    />
                    {error.title && (
                      <p className="mt-1 text-sm text-red-500">{error.title}</p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="description"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Description
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors min-h-[120px]"
                      placeholder="Describe your issue"
                    ></textarea>
                    {error.description && (
                      <p className="mt-1 text-sm text-red-500">{error.description}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Attachments
                    </label>
                    <div className="relative border-2 border-dashed border-gray-300 rounded-lg hover:border-primary/50 transition-colors min-h-[120px]">
                      {formData.attachment.length === 0 ? (
                        <div 
                          className="absolute inset-0 flex flex-col items-center justify-center cursor-pointer"
                          onClick={() => document.getElementById("header").click()}
                        >
                          <CiCirclePlus className="h-8 w-8 text-gray-400" />
                          <p className="mt-2 text-sm text-gray-500">
                            Click to add attachments
                          </p>
                        </div>
                      ) : (
                        <div className="p-4">
                          <div className="space-y-2">
                            {formData.attachment.map((file, index) => (
                              <div
                                key={index}
                                className="flex items-center justify-between bg-gray-50 p-2 rounded-lg group hover:bg-gray-100 transition-colors"
                              >
                                <p className="text-sm text-gray-600 truncate max-w-[80%]">
                                  {file.name}
                                </p>
                                <div className="flex items-center gap-2">
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      removeAttachment(index);
                                    }}
                                    className="text-gray-400 hover:text-red-500 transition-colors p-1 rounded-full hover:bg-gray-200 z-20"
                                  >
                                    <IoCloseCircleOutline className="h-5 w-5" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                          <div 
                            className="mt-3 flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors cursor-pointer"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              document.getElementById("header").click();
                            }}
                          >
                            <CiCirclePlus className="h-5 w-5" />
                            <span>Add more</span>
                          </div>
                        </div>
                      )}
                      
                      <input
                        type="file"
                        id="header"
                        name="header"
                        onChange={handleFileChange}
                        onClick={(e) => e.stopPropagation()}
                        className="absolute inset-0 w-full h-full opacity-0 pointer-events-none"
                        accept=".jpg, .png, .pdf, .doc, .docx"
                        multiple
                      />
                    </div>
                    {error.file && (
                      <p className="mt-1 text-sm text-red-500">{error.file}</p>
                    )}
                  </div>

                  <div className="flex justify-end space-x-4 pt-4">
                    <button
                      type="button"
                      onClick={handleCancel}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 focus:outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? "Submitting..." : "Submit"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {submissionStatus && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="w-full max-w-sm bg-white rounded-2xl shadow-xl p-6">
              {submissionStatus === "success" ? (
                <div className="text-center">
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100 mb-4">
                    <FaCheck className="h-6 w-6 text-green-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">
                    Issue Submitted Successfully
                  </h2>
                  <p className="text-gray-600 mb-6">
                    Our team will address your issue as soon as possible.
                  </p>
                  <button
                    onClick={closePopup}
                    className="w-full px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 focus:outline-none transition-colors"
                  >
                    Close
                  </button>
                </div>
              ) : (
                <div className="text-center">
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100 mb-4">
                    <FaExclamationTriangle className="h-6 w-6 text-red-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">
                    Submission Failed
                  </h2>
                  <p className="text-gray-600 mb-6">
                    Please try submitting your issue again.
                  </p>
                  <button
                    onClick={handleResubmit}
                    className="w-full px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 focus:outline-none transition-colors"
                  >
                    Try Again
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
export default HelpCenter;

// const HelpCenter = (className) => {
//   // const [isOpen, setIsOpen] = useState(false);
//   const [isFormVisible, setFormVisible] = useState(false);
//   const [submissionStatus, setSubmissionStatus] = useState(null);
//   const [formData, setFormData] = useState({
//     title: "",
//     description: "",
//     attachment: null,
//   });

//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setFormData({ ...formData, [name]: value });
//   };

//   const toggleForm = () => {
//         setFormVisible(!isFormVisible);
//       };

//   const handleFileChange = (e) => {
//     setFormData({ ...formData, attachment: e.target.files[0] });
//   };

//   const handleSubmit = (e) => {
//     e.preventDefault();
//     // console.log("Form Data:", formData);
//     // alert("Form submitted!");
//     // setFormVisible(false);
//     if (formData.title && formData.description) {
//       setSubmissionStatus("success");
//     } else {
//       setSubmissionStatus("failure");
//     }
//     setFormVisible(false);
//   };

//   const closePopup = () => {
//     setSubmissionStatus(null);
//   };

//   const handleCancel = () => {
//     setFormData({
//       title: "",
//       description: "",
//       attachment: null,
//     });
//     setFormVisible(false);
//   };

//   return (
//     <div className="flex items-center justify-center min-h-screen bg-gray-100">
//       {/* Button to open the popup */}
//       {/* <button
//         onClick={() => setIsOpen(true)}
//         className="px-4 py-2 text-white bg-orange-500 rounded hover:bg-orange-600"
//       >
//         Report an Issue
//       </button> */}

//       {isFormVisible ?
//         ( <div className="fixed w-screen top-0 h-screen z-50  inset-0 backdrop-filter overflow-hidden backdrop-blur-sm bg-opacity-40">
//           </div> ) : null}
//         <div
//           className={classNames(
//             `fixed  md:bottom-16 lg:bottom-16
//               ${
//                 window.location.pathname.includes("/company")
//                   ? window.location.pathname.includes("/job-post")
//                     ? "bottom-[11%]"
//                     : "bottom-[11%]"
//                   : "bottom-[13%]"
//               }
//               md:-right-6 lg:-right-6 -right-6 z-50`, className)
//           }
//         >

//       {isFormVisible ? (
//           <div
//             className="bg-primary text-xs text-white flex items-center gap-2 z-50  py-2 px-5 cursor-pointer -rotate-90"
//             onClick={toggleForm}
//           >
//             <AiOutlineArrowDown className="" />
//             <p>Close</p>
//           </div>
//         ) : (
//           // <div
//           //     className="bg-primary h-14 w-14 normal-shadow text-white rounded-full p-3 cursor-pointer"
//           //     onClick={toggleForm}
//           // >
//           //     <MdSupportAgent className='w-8 h-8 m-auto ' />
//           // </div>
//           // <div className='h-16 w-16 p-3 flex justify-center items-center  rounded-full'>
//           <div
//             className="bg-primary text-xs text-white flex items-center gap-2 p-2 cursor-pointer -rotate-90"
//             onClick={toggleForm}
//           >
//             <AiOutlineBug className="" />
//             <p>Have issue</p>
//           </div>
//         )
//       }

//       {/* Popup/Modal */}
//       {isFormVisible && (
//         <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
//           <div className="w-full max-w-md p-6 bg-white rounded shadow-lg">
//             <h2 className="text-2xl font-semibold text-gray-800 mb-4">
//               Report your issue
//             </h2>

//             <form onSubmit={handleSubmit}>
//               <div className="mb-4">
//                 <label
//                   htmlFor="title"
//                   className="block text-sm font-medium text-gray-700 mb-2"
//                 >
//                   Title of the issue
//                 </label>
//                 <input
//                   type="text"
//                   id="title"
//                   name="title"
//                   value={formData.title}
//                   onChange={handleInputChange}
//                   className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
//                   placeholder="Enter the title of the issue"
//                 />
//               </div>

//               <div className="mb-4">
//                 <label
//                   htmlFor="description"
//                   className="block text-sm font-medium text-gray-700 mb-2"
//                 >
//                   Description
//                 </label>
//                 <textarea
//                   id="description"
//                   name="description"
//                   value={formData.description}
//                   onChange={handleInputChange}
//                   className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
//                   placeholder="Describe your issue"
//                   rows="4"
//                 ></textarea>
//               </div>

//               <div className="mb-4">
//                 <label
//                   htmlFor="attachment"
//                   className="block text-sm font-medium text-gray-700 mb-2"
//                 >
//                   Attachment
//                 </label>
//                 <input
//                   type="file"
//                   id="attachment"
//                   onChange={handleFileChange}
//                   className="w-full"
//                 />
//               </div>

//               <div className="flex justify-end space-x-4">
//                 <button
//                   type="button"
//                   onClick={handleCancel}
//                   className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded hover:bg-gray-300 focus:outline-none"
//                 >
//                   Cancel
//                 </button>
//                 <button
//                   type="submit"
//                   className="px-4 py-2 text-sm font-medium text-white bg-orange-500 rounded hover:bg-orange-600 focus:outline-none"
//                 >
//                   Submit
//                 </button>
//               </div>
//             </form>
//           </div>
//         </div>)
//       }

//        {submissionStatus && (
//         <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
//           <div className="w-full max-w-sm p-6 bg-white rounded-lg shadow-lg">
//             {submissionStatus === "success" ? (
//               <div className="text-center">
//                 <div className="text-green-500 text-5xl mb-4">✔</div>
//                 <h2 className="text-xl font-semibold text-orange-500 mb-2">
//                   Your report was successfully submitted
//                 </h2>
//                 <p className="text-gray-600">
//                   Our team will address your issue as soon as possible.
//                 </p>
//                 <button
//                   onClick={closePopup}
//                   className="mt-4 px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600"
//                 >
//                   Close
//                 </button>
//               </div>
//             ) : (
//               <div className="text-center">
//                 <div className="text-red-500 text-5xl mb-4">⚠</div>
//                 <h2 className="text-xl font-semibold text-red-500 mb-2">
//                   Sorry! Your report hasn't been submitted
//                 </h2>
//                 <p className="text-gray-600">Try to resubmit your issue.</p>
//                 <button
//                   onClick={closePopup}
//                   className="mt-4 px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600"
//                 >
//                   Re-submit
//                 </button>
//               </div>
//             )}
//           </div>
//         </div>
//       )}

//       </div>
//     </div>
//   );
// };

// export default HelpCenter;
